import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV, PRODUCT_CATEGORY_DEFS } from "../data/site";
import { Logo } from "./Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.documentElement.classList.toggle("menu-open", open);
    return () => document.documentElement.classList.remove("menu-open");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-md border-b border-slate-200"
            : "bg-white/40 backdrop-blur-sm border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" data-testid="nav-logo" aria-label="STERILTRON home">
              <Logo />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) =>
                n.label === "Products" ? (
                  <div key={n.to} className="relative group" data-testid="nav-products-group">
                    <NavLink
                      to={n.to}
                      data-testid="nav-link-products"
                      className={({ isActive }) =>
                        `flex items-center gap-1 px-3.5 py-2 text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                          isActive ? "text-brand-800" : "text-slate-600 group-hover:text-brand-800"
                        }`
                      }
                    >
                      Products
                      <ChevronDown size={13} className="transition-transform group-hover:rotate-180" />
                    </NavLink>
                    <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                      <div className="w-64 bg-white border border-slate-200 shadow-xl py-2" data-testid="products-dropdown">
                        {PRODUCT_CATEGORY_DEFS.map((d) => (
                          <Link
                            key={d.slug}
                            to={`/products?category=${d.slug}`}
                            data-testid={`products-dropdown-${d.slug}`}
                            className="block px-5 py-3 text-[13px] font-medium text-slate-700 hover:bg-brand-50 hover:text-brand-800 transition-colors border-l-2 border-transparent hover:border-brand-800"
                          >
                            {d.navLabel}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `px-3.5 py-2 text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? "text-brand-800" : "text-slate-600 hover:text-brand-800"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                )
              )}
            </nav>

            <div className="hidden lg:block">
              <Link
                to="/contact"
                data-testid="nav-cta"
                className="group inline-flex items-center gap-1.5 bg-brand-800 text-white px-6 py-3 text-[13px] font-semibold uppercase tracking-wider hover:bg-brand-900 transition-colors"
              >
                Request Info
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-brand-900"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-brand-900 pt-24 px-6 overflow-y-auto pb-10"
          >
            <nav className="flex flex-col">
              {NAV.map((n, i) =>
                n.label === "Products" ? (
                  <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }} className="border-b border-white/10">
                    <div className="flex items-center justify-between py-4">
                      <NavLink to={n.to} data-testid="mobile-nav-link-products" className="text-white font-heading text-2xl font-semibold">
                        Products
                      </NavLink>
                      <button
                        data-testid="mobile-products-toggle"
                        onClick={() => setMobileProducts((v) => !v)}
                        className="p-2 text-white"
                        aria-label="Toggle products submenu"
                      >
                        <ChevronDown size={22} className={`transition-transform ${mobileProducts ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    <AnimatePresence>
                      {mobileProducts && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <div className="pb-4 pl-4 flex flex-col">
                            {PRODUCT_CATEGORY_DEFS.map((d) => (
                              <Link
                                key={d.slug}
                                to={`/products?category=${d.slug}`}
                                data-testid={`mobile-products-${d.slug}`}
                                onClick={() => setOpen(false)}
                                className="py-2.5 text-brand-100 text-base border-l border-white/15 pl-4 hover:text-white transition-colors"
                              >
                                {d.navLabel}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div
                    key={n.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    <NavLink
                      to={n.to}
                      data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="block py-4 border-b border-white/10 text-white font-heading text-2xl font-semibold"
                    >
                      {n.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-brand-900 px-6 py-4 font-semibold uppercase tracking-wider"
              >
                Request Info <ArrowUpRight size={16} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
